'use client';

import { Box, Typography, useTheme } from '@mui/material';

import { useT } from '@/hooks';

interface ChanceIndicatorProps {
  percentage: string;
}

export function ChanceIndicator({ percentage }: ChanceIndicatorProps) {
  const theme = useTheme();
  const { t } = useT();
  const isDarkMode = theme.palette.mode === 'dark';
  const numericValue = parseFloat(percentage.replace('%', '').replace('<', ''));
  const isHigh = numericValue >= 50;
  const progress = Math.min(100, Math.max(0, numericValue));

  const size = 56;
  const strokeWidth = 5;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  let progressColor: string;
  if (numericValue === 42) {
    progressColor = '#eab308';
  } else {
    progressColor = isHigh ? '#22c55e' : '#ef4444';
  }
  const backgroundColor = isDarkMode ? '#374151' : '#e5e7eb';
  const textColor = isDarkMode ? '#ffffff' : '#111827';
  const labelColor = isDarkMode ? '#9ca3af' : '#6b7280';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        width: size,
        height: size + 32,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
            opacity={0.2}
          />

          {progress > 0 && (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={progressColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              style={{
                transition: 'stroke-dashoffset 0.5s ease',
                filter: `drop-shadow(0 0 4px ${progressColor}50)`,
              }}
            />
          )}
        </svg>

        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              color: textColor,
              lineHeight: 1,
              letterSpacing: '-0.01em',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            {percentage}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: '0.625rem',
          color: labelColor,
          fontWeight: 500,
          lineHeight: 1.2,
          mt: 0.75,
          textAlign: 'center',
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
        }}
      >
        {t('market.chance')}
      </Typography>
    </Box>
  );
}
